// Controller responsible for address selection and validation
import ApplicationController from '../controllers/application_controller';
import Logger from '../lib/logger';
import { parseCoordinates, roundCoordinate, formatDD } from '../utils/coordinate_parser';
import { geocodeAddress, reverseGeocode, buildAddressString as buildAddressStr, parseAddressComponents, fetchCEP, applyCEPMask } from '../utils/geocoder';
import { initMap, isGoogleMapsLoaded, createMarker, cleanupMap, setupMapClick, setupMarkerListeners, centerOnMarker } from '../utils/google_maps_helper';
import { findSibling, selectOption, hideResults, parseSelection, buildSearchUrl, displayResults } from '../utils/street_autocomplete';
import { showSuccess, showError } from '../utils/toast';
function parseLocalFloat(val) {
    if (val === undefined || val === null)
        return NaN;
    const clean = val.toString().trim();
    if (!/^-?\d+([.,]\d+)?$/.test(clean))
        return NaN;
    return parseFloat(clean.replace(',', '.'));
}
class AddressController extends ApplicationController {
    constructor() {
        super(...arguments);
        this.isUpdatingFromMarker = false;
        this.map = null;
        this.marker = null;
        this.debugId = '';
        this.mouseDown = false;
    }
    initialize() {
        this.debouncedFetchResults = super.debounce(this.fetchResults.bind(this), this.debounceDelayValue);
    }
    connect() {
        super.connect();
        this.isUpdatingFromMarker = false;
        this.update();
        if (this.hasValidCoordinates()) {
            this.scheduleInitMap('connect');
        }
    }
    disconnect() {
        super.disconnect();
        if (this.initTimeout) {
            clearTimeout(this.initTimeout);
        }
        if (this.updateMarkerTimeout) {
            clearTimeout(this.updateMarkerTimeout);
        }
        if (this.onMapsLoaded) {
            window.removeEventListener('google-maps-loaded', this.onMapsLoaded);
        }
        if (this.hasMapdivTarget) {
            cleanupMap(this.mapdivTarget);
        }
        this.map = null;
        this.marker = null;
        Logger.debug(`[AddressController ${this.debugId}] Map instance destroyed`);
    }
    scheduleInitMap(source = 'unknown') {
        if (this.initTimeout)
            clearTimeout(this.initTimeout);
        this.initTimeout = setTimeout(() => {
            Logger.debug(`[AddressController ${this.debugId}] Initializing map (Source: ${source})`);
            this.initMap();
        }, 200);
    }
    hasValidCoordinates() {
        if (!this.hasLongitudeTarget || !this.hasLatitudeTarget)
            return false;
        const lat = parseLocalFloat(this.latitudeTarget.value);
        const lng = parseLocalFloat(this.longitudeTarget.value);
        return !isNaN(lat) && !isNaN(lng) && lat >= -90.0 && lat <= 90.0 && lng >= -180.0 && lng <= 180.0;
    }
    initMap() {
        if (!isGoogleMapsLoaded()) {
            Logger.debug('[AddressController] Google Maps API not yet loaded, waiting for event...');
            if (this.onMapsLoaded) {
                window.removeEventListener('google-maps-loaded', this.onMapsLoaded);
            }
            this.onMapsLoaded = () => {
                Logger.debug(`[AddressController ${this.debugId}] Google Maps loaded event received`);
                this.initMap();
            };
            window.addEventListener('google-maps-loaded', this.onMapsLoaded, { once: true });
            return;
        }
        if (this.map) {
            Logger.warn(`[AddressController ${this.debugId}] Map already exists, skipping`);
            return;
        }
        if (!this.hasMapdivTarget)
            return;
        const currentPosition = this.hasValidCoordinates() ? this.getCurrentPosition() : { lat: -23.55, lng: -46.63 };
        if (!this.debugId)
            this.debugId = Math.random().toString(36).substr(2, 9);
        this.map = initMap(this.mapdivTarget, {
            center: currentPosition,
            zoom: this.hasValidCoordinates() ? 18 : 4
        });
        if (!this.map)
            return;
        setupMapClick(this.map, (position) => this.handleMapClick(position));
        if (this.hasValidCoordinates()) {
            this.marker = createMarker(this.map, currentPosition);
            if (this.marker) {
                setupMarkerListeners(this.marker, () => {
                    const pos = this.marker.getPosition();
                    this.updateCoordinateInputs({ lat: pos.lat(), lng: pos.lng() });
                }, this.map);
            }
            centerOnMarker(this.map, this.marker);
        }
    }
    getCurrentPosition() {
        return {
            lat: parseLocalFloat(this.latitudeTarget.value),
            lng: parseLocalFloat(this.longitudeTarget.value)
        };
    }
    handleMapClick(position) {
        this.updateCoordinateInputs(position);
        if (this.marker) {
            this.marker.setPosition(position);
        }
        else {
            this.marker = createMarker(this.map, position);
            if (this.marker) {
                setupMarkerListeners(this.marker, () => {
                    const pos = this.marker.getPosition();
                    this.updateCoordinateInputs({ lat: pos.lat(), lng: pos.lng() });
                }, this.map);
            }
        }
        this.map.setCenter(position);
        this.map.setZoom(18);
    }
    updateCoordinateInputs(position) {
        this.isUpdatingFromMarker = true;
        // Check if position is a Google Maps LatLng object or a plain object
        const latVal = typeof position.lat === 'function' ? position.lat() : position.lat;
        const lngVal = typeof position.lng === 'function' ? position.lng() : position.lng;
        const lat = roundCoordinate(latVal);
        const lng = roundCoordinate(lngVal);
        if (this.hasLatitudeTarget)
            this.latitudeTarget.value = lat.toString();
        if (this.hasLongitudeTarget)
            this.longitudeTarget.value = lng.toString();
        this.updateCoordinatesInputDisplay();
        if (this.updateMarkerTimeout)
            clearTimeout(this.updateMarkerTimeout);
        this.updateMarkerTimeout = setTimeout(() => {
            this.isUpdatingFromMarker = false;
        }, 100);
    }
    refreshMarker() {
        if (!this.hasValidCoordinates() || this.isUpdatingFromMarker)
            return;
        const position = this.getCurrentPosition();
        if (this.hasLatitudeTarget)
            this.latitudeTarget.value = position.lat.toFixed(6);
        if (this.hasLongitudeTarget)
            this.longitudeTarget.value = position.lng.toFixed(6);
        if (this.marker) {
            this.marker.setPosition(position);
            this.map.setCenter(position);
        }
        else if (this.map) {
            this.marker = createMarker(this.map, position);
            if (this.marker) {
                setupMarkerListeners(this.marker, () => {
                    const pos = this.marker.getPosition();
                    this.updateCoordinateInputs({ lat: pos.lat(), lng: pos.lng() });
                }, this.map);
            }
            centerOnMarker(this.map, this.marker);
        }
        else {
            this.initMap();
        }
        this.update();
    }
    // CEP lookups
    async pesquisaCEP() {
        if (!this.hasZipcodeTarget)
            return;
        const cep = this.zipcodeTarget.value?.trim();
        if (!cep)
            return;
        try {
            const data = await fetchCEP(cep);
            Logger.debug('CEP found');
            this.updateAddressValues(data);
        }
        catch (error) {
            Logger.error('Error fetching CEP', error);
        }
    }
    updateAddressValues(data) {
        if (!data || !data.state) {
            Logger.warn('updateAddressValues: invalid data', { data });
            return;
        }
        if (this.hasUfTarget)
            this.ufTarget.value = data.state || '';
        if (this.hasCityTarget)
            this.cityTarget.value = data.city || '';
        if (data.address && this.hasStreetTarget) {
            this.streetTarget.value = data.address;
            if (this.hasStreetnumberTarget)
                this.streetnumberTarget.value = '';
        }
        if (this.hasNeighborhoodTarget)
            this.neighborhoodTarget.value = data.neighborhood || '';
        if (data.zip_code && this.hasZipcodeTarget)
            this.zipcodeTarget.value = data.zip_code || '';
        if (this.hasHiddenTarget)
            this.hiddenTarget.value = '';
        this.clearCoordinates();
    }
    clearCoordinates() {
        if (this.hasLatitudeTarget)
            this.latitudeTarget.value = '';
        if (this.hasLongitudeTarget)
            this.longitudeTarget.value = '';
        if (this.hasHiddenTarget)
            this.hiddenTarget.value = '';
    }
    handleEntitySelected(event) {
        Logger.debug('Address Controller received entity-selected event', { detail: event.detail });
        const data = event.detail;
        if (!data)
            return;
        this.updateAddressValues({
            state: data.state_uf,
            city: data.city,
            address: data.street,
            neighborhood: data.neighborhood
        });
        if (this.hasZipcodeTarget && data.zip_code)
            this.zipcodeTarget.value = data.zip_code;
        if (this.hasStreetnumberTarget && data.street_number)
            this.streetnumberTarget.value = data.street_number;
        if (this.hasStreetTarget && data.street)
            this.streetTarget.value = data.street;
        if (this.hasFulladdressTarget && data.full_address)
            this.fulladdressTarget.value = data.full_address;
        if (data.latitude != null && data.latitude !== '' && data.longitude != null && data.longitude !== '') {
            if (this.hasLatitudeTarget)
                this.latitudeTarget.value = data.latitude;
            if (this.hasLongitudeTarget)
                this.longitudeTarget.value = data.longitude;
            this.refreshMarker();
            this.updateCoordinatesInputDisplay();
        }
        else {
            if (this.hasValidCoordinates()) {
                this.refreshMarker();
                this.updateCoordinatesInputDisplay();
            }
            else {
                this.clearCoordinates();
            }
        }
        if (this.hasZipcodeTarget)
            this.zipcodeTarget.dispatchEvent(new Event('change', { bubbles: true }));
        if (this.hasStreetTarget)
            this.streetTarget.dispatchEvent(new Event('change', { bubbles: true }));
        if (!data.latitude && this.hasStreetnumberTarget)
            this.streetnumberTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }
    // Geocoding methods
    async geosearch() {
        this.clearHiddenValue();
        if (this.googleApiKeyValue && this.hasStreetTarget && this.streetTarget.value) {
            const address = buildAddressStr(this.streetTarget.value, this.hasStreetnumberTarget ? this.streetnumberTarget.value : '', this.hasNeighborhoodTarget ? this.neighborhoodTarget.value : '', this.hasCityTarget ? this.cityTarget.value : '', this.hasUfTarget ? this.ufTarget.value : '');
            if (address.trim()) {
                await this.geocodeAndSetCoordinates(address);
            }
        }
    }
    async geocodeAndSetCoordinates(address) {
        try {
            const result = await geocodeAddress(this.googleApiKeyValue, address);
            if (result) {
                if (this.hasLatitudeTarget)
                    this.latitudeTarget.value = result.coordinates.lat.toString();
                if (this.hasLongitudeTarget)
                    this.longitudeTarget.value = result.coordinates.lng.toString();
                this.refreshMarker();
                this.updateCoordinatesInputDisplay();
            }
            else {
                Logger.warn('Geocoding returned no results', { address });
            }
        }
        catch (error) {
            Logger.warn('Geocoding failed', error);
        }
    }
    async addrSearch() {
        this.clearHiddenValue();
        if (!this.hasFulladdressTarget)
            return;
        const address = this.fulladdressTarget.value?.trim();
        if (!address)
            return;
        try {
            const result = await geocodeAddress(this.googleApiKeyValue, address);
            if (result) {
                if (this.hasLatitudeTarget)
                    this.latitudeTarget.value = result.coordinates.lat.toString();
                if (this.hasLongitudeTarget)
                    this.longitudeTarget.value = result.coordinates.lng.toString();
                this.refreshMarker();
                this.updateCoordinatesInputDisplay();
                this.fulladdressTarget.value = result.formattedAddress;
                const parsed = parseAddressComponents(result.addressComponents);
                if (parsed.street && this.hasStreetTarget)
                    this.streetTarget.value = parsed.street;
                if (parsed.streetnumber && this.hasStreetnumberTarget)
                    this.streetnumberTarget.value = parsed.streetnumber;
                if (parsed.neighborhood && this.hasNeighborhoodTarget)
                    this.neighborhoodTarget.value = parsed.neighborhood;
                if (parsed.city && this.hasCityTarget)
                    this.cityTarget.value = parsed.city;
                if (parsed.uf && this.hasUfTarget)
                    this.ufTarget.value = parsed.uf;
                if (parsed.zipcode && this.hasZipcodeTarget)
                    this.zipcodeTarget.value = parsed.zipcode;
            }
        }
        catch (error) {
            Logger.error('Address search failed', error);
        }
    }
    // Coordinate parsing methods
    parseCoordinatesInput() {
        if (!this.hasCoordinatesInputTarget)
            return;
        const input = this.coordinatesInputTarget.value?.trim();
        if (!input)
            return;
        try {
            const coordinates = parseCoordinates(input);
            if (coordinates) {
                if (this.hasLatitudeTarget)
                    this.latitudeTarget.value = coordinates.lat.toString();
                if (this.hasLongitudeTarget)
                    this.longitudeTarget.value = coordinates.lng.toString();
                this.refreshMarker();
            }
            else {
                this.showCoordinateError('Invalid coordinate format');
            }
        }
        catch (error) {
            Logger.error('Coordinate parsing error', error);
            this.showCoordinateError('Error parsing coordinates');
        }
    }
    showCoordinateError(message) {
        Logger.warn('Coordinate Error', { message });
        if (this.hasCoordinatesInputTarget)
            this.coordinatesInputTarget.value = '';
    }
    validateCoordinatesInput() {
        if (!this.hasCoordinatesInputTarget)
            return true;
        const input = this.coordinatesInputTarget.value?.trim();
        if (!input)
            return true;
        return parseCoordinates(input) !== null;
    }
    // Reverse geocoding methods
    async fetchAddressFromCoordinates() {
        if (!this.hasValidCoordinates()) {
            this.showCoordinateError('Please enter valid coordinates first');
            return;
        }
        try {
            const lat = parseFloat(this.latitudeTarget.value);
            const lng = parseFloat(this.longitudeTarget.value);
            const addressData = await reverseGeocode(this.googleApiKeyValue, lat, lng);
            if (addressData) {
                if (this.hasFulladdressTarget)
                    this.fulladdressTarget.value = addressData.formattedAddress;
                const parsed = parseAddressComponents(addressData.addressComponents);
                if (parsed.street && this.hasStreetTarget)
                    this.streetTarget.value = parsed.street;
                if (parsed.streetnumber && this.hasStreetnumberTarget)
                    this.streetnumberTarget.value = parsed.streetnumber;
                if (parsed.neighborhood && this.hasNeighborhoodTarget)
                    this.neighborhoodTarget.value = parsed.neighborhood;
                if (parsed.city && this.hasCityTarget)
                    this.cityTarget.value = parsed.city;
                if (parsed.uf && this.hasUfTarget)
                    this.ufTarget.value = parsed.uf;
                if (parsed.zipcode && this.hasZipcodeTarget)
                    this.zipcodeTarget.value = parsed.zipcode;
                this.updateCoordinatesInputDisplay();
            }
            else {
                this.showCoordinateError('No address found for these coordinates');
            }
        }
        catch (error) {
            Logger.error('Reverse geocoding failed', error);
            this.showCoordinateError('Error fetching address from coordinates');
        }
    }
    updateCoordinatesInputDisplay() {
        if (!this.hasCoordinatesInputTarget)
            return;
        const lat = parseLocalFloat(this.latitudeTarget.value);
        const lng = parseLocalFloat(this.longitudeTarget.value);
        if (!isNaN(lat) && !isNaN(lng)) {
            this.coordinatesInputTarget.value = formatDD(lat, lng);
        }
    }
    // Autocomplete functionality
    onStreetBlur() {
        if (this.mouseDown)
            return;
        if (this.hasResultsTarget)
            hideResults(this.resultsTarget);
    }
    commit(selected) {
        if (selected.getAttribute('aria-disabled') === 'true')
            return;
        if (selected instanceof HTMLAnchorElement) {
            selected.click();
            return;
        }
        const fields = parseSelection(selected);
        if (this.hasStreetTarget)
            this.streetTarget.value = fields.street;
        if (fields.streetnumber !== '' && this.hasStreetnumberTarget)
            this.streetnumberTarget.value = fields.streetnumber;
        if (fields.zipcode !== '' && this.hasZipcodeTarget)
            this.zipcodeTarget.value = fields.zipcode;
        if (fields.neighborhood !== '' && this.hasNeighborhoodTarget)
            this.neighborhoodTarget.value = fields.neighborhood;
        if (fields.city !== '' && this.hasCityTarget)
            this.cityTarget.value = fields.city;
        if (fields.uf !== '' && this.hasUfTarget)
            this.ufTarget.value = fields.uf;
        if (fields.latitude !== '' && this.hasLatitudeTarget)
            this.latitudeTarget.value = fields.latitude;
        if (fields.longitude !== '' && this.hasLongitudeTarget)
            this.longitudeTarget.value = fields.longitude;
        if (this.hasHiddenTarget)
            this.hiddenTarget.value = fields.hiddenValue;
        if (fields.latitude !== '' && fields.longitude !== '') {
            this.refreshMarker();
            this.updateCoordinatesInputDisplay();
        }
        if (this.hasResultsTarget)
            hideResults(this.resultsTarget);
    }
    onResultsClick(event) {
        if (!(event.target instanceof Element))
            return;
        const selected = event.target.closest('[role="option"]');
        if (selected)
            this.commit(selected);
    }
    onResultsMouseDown() {
        this.mouseDown = true;
        if (this.hasResultsTarget) {
            this.resultsTarget.addEventListener('mouseup', () => (this.mouseDown = false), { once: true });
        }
    }
    onStreetChange() {
        this.element.removeAttribute('value');
        if (this.debouncedFetchResults) {
            this.debouncedFetchResults();
        }
        else {
            this.fetchResults();
        }
    }
    onKeydown(event) {
        const keyHandlers = {
            'Escape': () => this.handleEscapeKey(event),
            'ArrowDown': () => this.handleArrowKey(event, true),
            'ArrowUp': () => this.handleArrowKey(event, false),
            'Tab': () => this.handleTabKey(event),
            'Enter': () => this.handleEnterKey(event)
        };
        const handler = keyHandlers[event.key];
        if (handler) {
            handler();
        }
    }
    handleEscapeKey(event) {
        if (this.hasResultsTarget && !this.resultsTarget.hidden) {
            hideResults(this.resultsTarget);
            event.stopPropagation();
            event.preventDefault();
        }
    }
    handleArrowKey(event, isDown) {
        if (!this.hasResultsTarget || !this.hasStreetTarget)
            return;
        const item = findSibling(this.resultsTarget, isDown);
        if (item) {
            selectOption(this.resultsTarget, this.streetTarget, item);
            event.preventDefault();
        }
    }
    handleTabKey(event) {
        if (!this.hasResultsTarget)
            return;
        const selected = this.resultsTarget.querySelector('[aria-selected="true"]');
        if (selected) {
            this.commit(selected);
        }
    }
    handleEnterKey(event) {
        if (!this.hasResultsTarget)
            return;
        const selected = this.resultsTarget.querySelector('[aria-selected="true"]');
        if (selected && !this.resultsTarget.hidden) {
            this.commit(selected);
            event.preventDefault();
        }
    }
    async fetchResults() {
        if (!this.hasStreetTarget)
            return;
        const query = this.streetTarget.value?.trim();
        if (this.shouldHideAutocomplete(query)) {
            if (this.hasResultsTarget)
                hideResults(this.resultsTarget);
            return;
        }
        if (!this.searchUrlValue) {
            if (this.hasResultsTarget)
                hideResults(this.resultsTarget);
            return;
        }
        try {
            const url = buildSearchUrl(this.searchUrlValue, query, this.hasUfTarget ? this.ufTarget.value : '', this.hasCityTarget ? this.cityTarget.value : '');
            // Dispatch loadstart event for Turbo indicators
            this.dispatchLoadStartEvent();
            const response = await this.safeFetch(url);
            const html = await response.text();
            if (this.hasResultsTarget)
                displayResults(this.resultsTarget, html);
            this.dispatchLoadEvents();
        }
        catch (error) {
            Logger.error('Search failed', error);
            this.dispatchErrorEvents();
        }
    }
    update() {
        if (this.hasCoordtextTarget && this.hasLatitudeTarget && this.hasLongitudeTarget) {
            const message = this.messageValue || 'Latitude: {latitude}, Longitude: {longitude}';
            this.coordtextTarget.textContent = message
                .replace('{latitude}', this.latitudeTarget.value)
                .replace('{longitude}', this.longitudeTarget.value);
        }
    }
    applyMask(input) {
        let target = input;
        if (input && input.target) {
            target = input.target;
        }
        if (!target || typeof target.value !== 'string')
            return;
        target.value = applyCEPMask(target.value);
    }
    restoreOriginalCoordinates(event) {
        const rawLat = event.params.originalLatitude !== undefined ? event.params.originalLatitude : event.currentTarget?.dataset?.addressOriginalLatitudeParam;
        const rawLng = event.params.originalLongitude !== undefined ? event.params.originalLongitude : event.currentTarget?.dataset?.addressOriginalLongitudeParam;
        if (rawLat !== undefined && rawLat !== null && rawLng !== undefined && rawLng !== null) {
            const latVal = parseLocalFloat(rawLat);
            const lngVal = parseLocalFloat(rawLng);
            const isLatValid = !isNaN(latVal) && isFinite(latVal) && latVal >= -90.0 && latVal <= 90.0;
            const isLngValid = !isNaN(lngVal) && isFinite(lngVal) && lngVal >= -180.0 && lngVal <= 180.0;
            if (isLatValid && isLngValid) {
                // Format to exactly 6 decimal places with dot separator (guaranteed by toFixed)
                const formattedLat = latVal.toFixed(6);
                const formattedLng = lngVal.toFixed(6);
                if (this.hasLatitudeTarget) {
                    this.latitudeTarget.value = formattedLat;
                    this.latitudeTarget.dispatchEvent(new Event('change', { bubbles: true }));
                }
                if (this.hasLongitudeTarget) {
                    this.longitudeTarget.value = formattedLng;
                    this.longitudeTarget.dispatchEvent(new Event('change', { bubbles: true }));
                }
                this.refreshMarker();
                this.updateCoordinatesInputDisplay();
                const alert = event.currentTarget?.closest('.alert');
                if (alert) {
                    alert.remove();
                }
                Logger.success('Coordinates restored and normalized from spreadsheet data', {
                    latitude: formattedLat,
                    longitude: formattedLng,
                    controller: this.constructor.name
                });
                showSuccess('Original spreadsheet coordinates restored successfully.');
            }
            else {
                Logger.warn('Invalid coordinate values provided for restore', {
                    rawLatitude: rawLat,
                    rawLongitude: rawLng,
                    controller: this.constructor.name
                });
                showError('Invalid original coordinates in spreadsheet data.');
            }
        }
    }
    close(event) {
        if (event)
            event.preventDefault();
        Logger.debug(`[AddressController ${this.debugId}] Close requested`);
        const frame = this.element.closest("turbo-frame");
        if (frame) {
            frame.innerHTML = "";
            frame.removeAttribute("src");
        }
    }
    clearHiddenValue() {
        if (this.hasHiddenTarget) {
            this.hiddenTarget.value = '';
        }
    }
    shouldHideAutocomplete(query) {
        return !query || query.length < this.minSearchLengthValue;
    }
    dispatchLoadStartEvent() {
        this.dispatch('loadstart');
    }
    dispatchLoadEvents() {
        this.dispatch('load');
        this.dispatch('loadend');
    }
    dispatchErrorEvents() {
        this.dispatch('error');
        this.dispatch('loadend');
    }
}
AddressController.targets = [
    'hidden',
    'zipcode',
    'street',
    'streetnumber',
    'neighborhood',
    'city',
    'uf',
    'latitude',
    'longitude',
    'coordtext',
    'mapdiv',
    'results',
    'fulladdress',
    'coordinatesInput'
];
AddressController.values = {
    message: String,
    googleApiKey: String,
    searchUrl: String,
    debounceDelay: { type: Number, default: 300 },
    minSearchLength: { type: Number, default: 3 }
};
export default AddressController;
